import { Button } from '@/components/atoms';
import {
  CalendarIcon,
  ClockIcon,
} from '@/components/atoms/icons';
import { Typography } from '@/components/atoms/Typography';
import { useSalesAgentContact, useUIColors } from '@/core/hooks';
import React from 'react';
import {
  Platform,
  Pressable,
  StyleSheet,
  View,
  useWindowDimensions,
} from 'react-native';
import { SHOW_SALES_AGENT_CONTACT } from '../constants/salesAgentVisibility';
import { useAppointmentLabels } from '../hooks/useAppointmentLabels';
import type { BookedAppointment } from '../types/appointment.types';
import { AppointmentSalesContact } from './AppointmentSalesContact';

export type AppointmentStatusCardVariant = 'default' | 'sheet';

export interface AppointmentStatusCardProps {
  appointment: BookedAppointment;
  onCancel: () => void;
  onReschedule: () => void;
  iconBackgroundColor: string;
  iconColor: string;
  /** sheet = mobile bottom sheet: title + 2×2 grid + actions */
  variant?: AppointmentStatusCardVariant;
}

const META_ICON_SIZE = 14;
const META_ICON_GAP = 6;
const LEADING_ICON_SIZE = 56;
const HEADER_GAP = 16;
/** Matches unit-details tablet band (768–1099) */
const SHEET_TABLET_MIN_WIDTH = 768;
const SHEET_TABLET_MAX_WIDTH = 1100;

export function AppointmentStatusCard({
  appointment,
  onCancel,
  onReschedule,
  iconBackgroundColor,
  iconColor,
  variant = 'default',
}: Readonly<AppointmentStatusCardProps>) {
  const colors = useUIColors();
  const labels = useAppointmentLabels();
  const isSheet = variant === 'sheet';
  const salesAgent = useSalesAgentContact();
  const dialColor = colors.appointmentBadgeText;

  const hasSalesAgent =
    salesAgent.name.length > 0 || salesAgent.phone.length > 0;
  const salesContact =
    SHOW_SALES_AGENT_CONTACT && hasSalesAgent ? (
      <AppointmentSalesContact
        label={salesAgent.name}
        phone={salesAgent.phone}
        color={dialColor}
        size="detail"
      />
    ) : null;

  if (isSheet) {
    return (
      <View
        style={[styles.card, styles.cardSheet, { backgroundColor: colors.backgroundLevel2 }]}
      >
        <Typography
          variant="headline"
          color="primary"
          style={styles.sheetTitle}
        >
          {labels.STATUS_TITLE}
        </Typography>

        <View
          style={[styles.dashedDivider, { borderBottomColor: colors.outlineDisabled }]}
          accessibilityElementsHidden
          importantForAccessibility="no-hide-descendants"
        />

        <View style={styles.sheetGrid}>
          <View style={styles.sheetRow}>
            <SheetField
              label={labels.CONFIRMED_DATE}
              value={appointment.dateDisplay}
              icon={
                <CalendarIcon size={META_ICON_SIZE} color={colors.fontSecondary} />
              }
            />
            <SheetField
              label={labels.CONFIRMED_TIME}
              value={appointment.timeLabel}
              icon={
                <ClockIcon size={META_ICON_SIZE} color={colors.fontSecondary} />
              }
            />
          </View>
          {salesContact ? (
            <View style={styles.sheetAgentRow}>
              <Typography
                variant="caption"
                color="secondary"
                style={styles.sheetMetaText}
              >
                {labels.CONFIRMED_AGENT}
              </Typography>
              {salesContact}
            </View>
          ) : null}
        </View>

        <SheetAppointmentActions
          cancelLabel={labels.STATUS_CANCEL}
          rescheduleLabel={labels.STATUS_RESCHEDULE}
          onCancel={onCancel}
          onReschedule={onReschedule}
        />
      </View>
    );
  }

  return (
    <View
      style={[styles.card, { backgroundColor: colors.backgroundLevel2 }]}
    >
      <View style={styles.headerRow}>
        <View style={[styles.iconTile, { backgroundColor: iconBackgroundColor }]}>
          <CalendarIcon size={24} color={iconColor} />
        </View>
        <View style={styles.headerText}>
          <Typography variant="headline" color="primary">
            {labels.STATUS_TITLE}
          </Typography>
          <View style={styles.metaBlock}>
            <View style={styles.metaRow}>
              <View style={styles.metaItem}>
                <CalendarIcon size={META_ICON_SIZE} color={colors.fontSecondary} />
                <Typography
                  variant="body"
                  color="secondary"
                  style={styles.metaText}
                >
                  {appointment.dateDisplay}
                </Typography>
              </View>
              <View style={styles.metaItem}>
                <ClockIcon size={META_ICON_SIZE} color={colors.fontSecondary} />
                <Typography
                  variant="body"
                  color="secondary"
                  style={styles.metaText}
                >
                  {appointment.timeLabel}
                </Typography>
              </View>
            </View>
            {salesContact}
          </View>
        </View>
      </View>

      <SidebarAppointmentActions
        cancelLabel={labels.STATUS_CANCEL}
        rescheduleLabel={labels.STATUS_RESCHEDULE}
        onCancel={onCancel}
        onReschedule={onReschedule}
        buttonBackgroundColor={colors.backgroundLevel2}
      />
    </View>
  );
}

function SheetAppointmentActions({
  cancelLabel,
  rescheduleLabel,
  onCancel,
  onReschedule,
}: Readonly<{
  cancelLabel: string;
  rescheduleLabel: string;
  onCancel: () => void;
  onReschedule: () => void;
}>) {
  const { width } = useWindowDimensions();
  const isTablet =
    width >= SHEET_TABLET_MIN_WIDTH && width < SHEET_TABLET_MAX_WIDTH;

  return (
    <View style={styles.sheetActionsRow}>
      <Pressable
        onPress={onCancel}
        accessibilityRole="button"
        accessibilityLabel={cancelLabel}
        style={[
          styles.sheetCancelLinkWrap,
          isTablet && styles.sheetActionHalf,
        ]}
      >
        <Typography
          variant="headline"
          color="highContrast"
          style={styles.sheetCancelLink}
        >
          {cancelLabel}
        </Typography>
      </Pressable>
      <View
        style={[
          styles.sheetPrimaryActionWrap,
          isTablet && styles.sheetActionHalf,
        ]}
      >
        <Button
          title={rescheduleLabel}
          variant="outline"
          fullWidth
          onPress={onReschedule}
          style={styles.sheetPrimaryActionButton}
          textStyle={styles.sheetPrimaryActionLabel}
        />
      </View>
    </View>
  );
}

function SidebarAppointmentActions({
  cancelLabel,
  rescheduleLabel,
  onCancel,
  onReschedule,
  buttonBackgroundColor,
}: Readonly<{
  cancelLabel: string;
  rescheduleLabel: string;
  onCancel: () => void;
  onReschedule: () => void;
  buttonBackgroundColor: string;
}>) {
  return (
    <View style={styles.sidebarActionsRow}>
      <View style={styles.sidebarActionHalf}>
        <Button
          title={cancelLabel}
          variant="outline"
          fullWidth
          onPress={onCancel}
          style={[
            styles.sidebarActionButton,
            { backgroundColor: buttonBackgroundColor },
          ]}
          textStyle={styles.sidebarActionLabel}
        />
      </View>
      <View style={styles.sidebarActionHalf}>
        <Button
          title={rescheduleLabel}
          variant="outline"
          fullWidth
          onPress={onReschedule}
          style={[
            styles.sidebarActionButton,
            { backgroundColor: buttonBackgroundColor },
          ]}
          textStyle={styles.sidebarActionLabel}
        />
      </View>
    </View>
  );
}


function SheetField({
  label,
  value,
  icon,
}: Readonly<{
  label: string;
  value: string;
  icon: React.ReactNode;
}>) {
  return (
    <View style={styles.sheetField}>
      <Typography
        variant="caption"
        color="secondary"
        style={styles.sheetMetaText}
      >
        {label}
      </Typography>
      <View style={styles.sheetValueRow}>
        {icon}
        <Typography
          variant="caption"
          color="secondary"
          style={styles.sheetMetaText}
          numberOfLines={1}
        >
          {value}
        </Typography>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    borderRadius: 16,
    paddingTop: 16,
    paddingHorizontal: 16,
    paddingBottom: 12,
    gap: 24,
    overflow: 'visible',
  },
  cardSheet: {
    gap: 16,
  },
  sheetTitle: {
    lineHeight: 16,
  },
  dashedDivider: {
    borderBottomWidth: 1,
    borderStyle: 'dashed',
    width: '100%',
  },
  sheetGrid: {
    gap: 16,
    // Above sheet actions so phone tooltip isn't covered by Reschedule.
    position: 'relative',
    zIndex: 2,
    overflow: 'visible',
  },
  sheetRow: {
    flexDirection: 'row',
    gap: 16,
    alignItems: 'flex-start',
  },
  sheetAgentRow: {
    gap: 6,
    minWidth: 0,
    overflow: 'visible',
  },
  sheetField: {
    flex: 1,
    minWidth: 0,
    gap: 6,
  },
  sheetMetaText: {
    flexShrink: 1,
    lineHeight: 12,
  },
  sheetValueRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: META_ICON_GAP,
    minWidth: 0,
  },
  sheetActionsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  sheetCancelLinkWrap: {
    flexShrink: 1,
    minWidth: 0,
    justifyContent: 'center',
  },
  sheetActionHalf: {
    flex: 1,
    flexShrink: 0,
  },
  sheetCancelLink: {
    lineHeight: 16,
    textDecorationLine: 'underline',
    ...(Platform.OS === 'web'
      ? ({ textUnderlineOffset: 6 } as object)
      : { paddingBottom: 2 }),
  },
  sheetPrimaryActionWrap: {
    flex: 1,
    minWidth: 0,
  },
  sheetPrimaryActionButton: {
    height: 48,
    borderRadius: 4,
    borderWidth: 2,
    padding: 12,
    backgroundColor: 'transparent',
  },
  sheetPrimaryActionLabel: {
    fontSize: 16,
    lineHeight: 16,
    textAlign: 'center',
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: HEADER_GAP,
    // Above action buttons so the phone tooltip can overflow onto them.
    position: 'relative',
    zIndex: 2,
    overflow: 'visible',
  },
  iconTile: {
    width: LEADING_ICON_SIZE,
    height: LEADING_ICON_SIZE,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
    flexShrink: 0,
  },
  headerText: {
    flex: 1,
    gap: 8,
    minWidth: 0,
    overflow: 'visible',
  },
  metaBlock: {
    gap: 12,
    minWidth: 0,
    overflow: 'visible',
  },
  metaRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 16,
  },
  metaItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: META_ICON_GAP,
    minWidth: 0,
  },
  metaText: {
    lineHeight: 22,
    flexShrink: 1,
  },
  sidebarActionsRow: {
    flexDirection: 'row',
    alignItems: 'stretch',
    gap: 12,
    // Do not set zIndex here — zIndex:0 creates a stacking context that
    // paints above headerRow (auto) and hides the overflowing phone tooltip.
  },
  sidebarActionHalf: {
    flex: 1,
    minWidth: 0,
  },
  sidebarActionButton: {
    borderRadius: 4,
  },
  sidebarActionLabel: {
    fontSize: 16,
    lineHeight: 16,
    textAlign: 'center',
  },
});

export default AppointmentStatusCard;
